import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SourcesStep, ReviewStep } from './WizardSteps';

interface Slide {
  slide_number: number;
  type: 'title' | 'content' | 'two_column' | 'quote';
  title: string;
  bullets?: string[];
  left_content?: string;
  right_content?: string;
  quote?: string;
  attribution?: string;
}

interface CreateWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type WizardStep = 'describe' | 'sources' | 'review';
type Tone = 'professional' | 'casual' | 'technical' | 'creative';

const TONES: { value: Tone; label: string }[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'technical', label: 'Technical' },
  { value: 'creative', label: 'Creative' },
];

export function CreateWizardDialog({ open, onOpenChange }: CreateWizardDialogProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<WizardStep>('describe');
  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('');
  const [tone, setTone] = useState<Tone>('professional');
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [generatedSlides, setGeneratedSlides] = useState<Slide[] | null>(null);

  const { data: documents } = useQuery({
    queryKey: ['documents-for-pres'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('document')
        .select('id, title')
        .is('deleted_at', null)
        .order('title');
      if (error) throw error;
      return data as { id: string; title: string }[];
    },
    enabled: open,
  });

  const generate = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('presentation-generate', {
        body: { topic, audience, tone, document_ids: selectedDocs.length > 0 ? selectedDocs : undefined },
      });
      if (error) throw error;
      return data as { slides: Slide[] };
    },
    onSuccess: (result) => {
      setGeneratedSlides(result.slides);
      queryClient.invalidateQueries({ queryKey: ['presentations'] });
    },
  });

  const resetAndClose = () => {
    setStep('describe');
    setTopic('');
    setAudience('');
    setTone('professional');
    setSelectedDocs([]);
    setGeneratedSlides(null);
    onOpenChange(false);
  };

  const handleStepNext = () => {
    if (step === 'describe') setStep('sources');
    else if (step === 'sources') {
      setStep('review');
      generate.mutate();
    }
  };

  const handleStepBack = () => {
    if (step === 'sources') setStep('describe');
    else if (step === 'review') setStep('sources');
  };

  const toggleDoc = (id: string) => {
    setSelectedDocs((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id],
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) resetAndClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === 'describe' && 'Describe Your Presentation'}
            {step === 'sources' && 'Select Sources'}
            {step === 'review' && 'Review & Generate'}
          </DialogTitle>
        </DialogHeader>

        {step === 'describe' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="pres-topic">Topic</Label>
              <Textarea
                id="pres-topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Describe the presentation topic..."
                rows={3}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="pres-audience">Audience</Label>
              <Input
                id="pres-audience"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder="e.g. Executive team, Engineering dept"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Tone</Label>
              <Select value={tone} onValueChange={(v) => setTone(v as Tone)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TONES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleStepNext} disabled={!topic.trim()}>Next</Button>
            </div>
          </div>
        )}

        {step === 'sources' && (
          <SourcesStep
            documents={documents ?? []}
            selectedDocs={selectedDocs}
            toggleDoc={toggleDoc}
            onBack={handleStepBack}
            onNext={handleStepNext}
          />
        )}

        {step === 'review' && (
          <ReviewStep
            isPending={generate.isPending}
            isError={generate.isError}
            slides={generatedSlides}
            onRetry={() => generate.mutate()}
            onBack={handleStepBack}
            onDone={resetAndClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
